<script setup lang="ts">
import { computed, type Component } from 'vue'
import {
  CalendarDays,
  Check,
  ClipboardCheck,
  FileText,
  GraduationCap,
  MapPin,
  NotebookPen,
} from 'lucide-vue-next'
import { ScheduleAccents } from '../palette'
import type { ScheduleItemDisplay } from '../scheduleItemDisplay'

/**
 * ScheduleItem — mirrors core/ui/scheduleitem/ScheduleItem.kt.
 *
 * Visual:
 *   ┌─────────┐  ╭──────────────────────────────────────╮
 *   │ 09:00   │  │ 🎓 LESSON                            │
 *   │ 10:00   │  │ Year 8 Oasis                          │
 *   │         │  │ 📍 Room 14 · Quadratics               │
 *   │         │  │ ✓ Plan ready    ✓ 96% present         │
 *   └─────────┘  ╰──────────────────────────────────────╯
 *
 * Card background and border are tinted by schedule TYPE
 * (lesson=indigo, assignment=amber, event=green) — colour signals
 * what the item is. Class colour is metadata, surfaced elsewhere.
 *
 * Variants:
 *   full    — Time gutter on the left (start + end stacked, or
 *             ALL DAY / DUE rail). Use in agenda lists.
 *   compact — Time inline above the headline. Use in week-strip
 *             columns where the column header already implies the day.
 */

const props = withDefaults(defineProps<{
  display: ScheduleItemDisplay
  variant?: 'full' | 'compact'
}>(), {
  variant: 'full',
})

defineEmits<{ click: [event: MouseEvent] }>()

interface Palette {
  ink: string
  tint: string
  typeLabel: string
  glyph: Component
}

const palette = computed<Palette>(() => {
  const kind = props.display.kind
  if (kind === 'lesson') {
    return { ink: ScheduleAccents.lesson.ink, tint: ScheduleAccents.lesson.tint, typeLabel: 'Lesson', glyph: GraduationCap }
  }
  if (kind === 'assignment') {
    return { ink: ScheduleAccents.assignment.ink, tint: ScheduleAccents.assignment.tint, typeLabel: 'Assignment', glyph: ClipboardCheck }
  }
  return { ink: ScheduleAccents.event.ink, tint: ScheduleAccents.event.tint, typeLabel: 'Event', glyph: CalendarDays }
})

const headline = computed(() => {
  const d = props.display
  if (d.kind === 'lesson') return d.className
  return d.title
})

interface MetaPart { text: string; isPlace: boolean }

const metaParts = computed<MetaPart[]>(() => {
  const d = props.display
  if (d.kind === 'lesson') {
    const parts: MetaPart[] = []
    if (d.room?.trim()) parts.push({ text: `Room ${d.room.trim()}`, isPlace: true })
    if (d.lessonTitle?.trim()) parts.push({ text: d.lessonTitle.trim(), isPlace: false })
    return parts
  }
  if (d.kind === 'event') {
    const parts: MetaPart[] = []
    if (d.location?.trim()) parts.push({ text: d.location.trim(), isPlace: true })
    return parts
  }
  return []
})

const secondaryChip = computed<string | null>(() => {
  const d = props.display
  if (d.kind === 'assignment') return d.categoryName?.trim() || null
  if (d.kind === 'event') return d.kindLabel?.trim() || null
  return null
})

interface Pill { icon: Component; label: string; tone: 'success' | 'neutral' | 'accent' }

const statusPills = computed<Pill[]>(() => {
  const d = props.display
  const pills: Pill[] = []
  if (d.kind === 'lesson') {
    if (d.showPlanStatus) {
      pills.push(
        d.planReady
          ? { icon: Check, label: 'Plan ready', tone: 'success' }
          : { icon: FileText, label: 'No plan', tone: 'neutral' },
      )
    }
    if (d.attendancePercent === null || d.attendancePercent === undefined) {
      pills.push({ icon: NotebookPen, label: 'Take register', tone: 'neutral' })
    } else if (d.attendancePercent === 100) {
      pills.push({ icon: Check, label: 'All present', tone: 'success' })
    } else {
      pills.push({ icon: Check, label: `${d.attendancePercent}% present`, tone: 'success' })
    }
  } else if (d.kind === 'assignment') {
    const marked = d.markedCount ?? 0
    const total = d.totalCount ?? 0
    if (marked > 0 || total > 0) {
      pills.push({
        icon: Check,
        label: total > 0 ? `${marked}/${total} marked` : `${marked} marked`,
        tone: 'accent',
      })
    }
  }
  return pills
})

const markedFraction = computed<number | null>(() => {
  const d = props.display
  if (d.kind !== 'assignment') return null
  const total = d.totalCount ?? 0
  const marked = d.markedCount ?? 0
  if (total <= 0 || marked <= 0) return null
  return Math.min(1, marked / total)
})

// ── Time formatting ─────────────────────────────────────────────────────────

const gutterLines = computed<{ primary: string; secondary?: string; railLabel?: string }>(() => {
  const d = props.display
  if (d.kind === 'assignment') {
    return { railLabel: 'DUE', primary: formatTime(d.due) }
  }
  if (d.kind === 'event' && d.allDay) {
    return { primary: 'ALL', secondary: 'DAY' }
  }
  if (d.kind === 'lesson' || d.kind === 'event') {
    return {
      primary: formatTime(d.start),
      ...(d.end ? { secondary: formatTime(d.end) } : {}),
    }
  }
  return { primary: '' }
})

const inlineTime = computed(() => {
  const d = props.display
  if (d.kind === 'assignment') return `Due ${formatTime(d.due)}`
  if (d.kind === 'event' && d.allDay) return 'All day'
  if (d.kind === 'lesson' || d.kind === 'event') {
    return d.end
      ? `${formatTime(d.start)} – ${formatTime(d.end)}`
      : formatTime(d.start)
  }
  return ''
})

function formatTime(d: Date): string {
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
}

// ── Styles ──────────────────────────────────────────────────────────────────

const cardStyle = computed(() => ({
  backgroundColor: palette.value.tint,
  borderColor: addAlpha(palette.value.ink, 0.15),
}))

const pillStyle = (tone: Pill['tone']) => {
  if (tone === 'success') {
    return { color: 'var(--success-ink)', backgroundColor: 'var(--success-soft)' }
  }
  if (tone === 'accent') {
    return { color: palette.value.ink, backgroundColor: addAlpha(palette.value.ink, 0.12) }
  }
  return { color: 'var(--ink-600)', backgroundColor: 'var(--ink-100)' }
}

const secondaryChipStyle = computed(() => ({
  color: palette.value.ink,
  backgroundColor: addAlpha(palette.value.ink, 0.14),
}))

const progressBarStyle = computed(() => ({
  backgroundColor: addAlpha(palette.value.ink, 0.18),
}))
const progressBarFillStyle = computed(() => ({
  backgroundColor: palette.value.ink,
  width: `${Math.round((markedFraction.value ?? 0) * 100)}%`,
}))

function addAlpha(hex: string, alpha: number): string {
  const h = hex.replace('#', '')
  const r = parseInt(h.substring(0, 2), 16)
  const g = parseInt(h.substring(2, 4), 16)
  const b = parseInt(h.substring(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const isClickable = computed(() => true)
</script>

<template>
  <div
    class="schedule-item"
    :class="[`schedule-item-${variant}`]"
    @click="(e) => isClickable && $emit('click', e)"
  >
    <!-- ── Time gutter (full only) ─────────────────────────────────────── -->
    <div v-if="variant === 'full'" class="gutter">
      <template v-if="gutterLines.railLabel">
        <span class="gutter-rail-label">{{ gutterLines.railLabel }}</span>
        <span class="gutter-primary tnum">{{ gutterLines.primary }}</span>
      </template>
      <template v-else>
        <span class="gutter-primary tnum">{{ gutterLines.primary }}</span>
        <span v-if="gutterLines.secondary" class="gutter-secondary tnum">
          {{ gutterLines.secondary }}
        </span>
      </template>
    </div>

    <!-- ── Tinted body ─────────────────────────────────────────────────── -->
    <div class="body" :style="cardStyle">
      <div class="eyebrow-row">
        <span class="type-label" :style="{ color: palette.ink }">
          <component :is="palette.glyph" :size="11" :stroke-width="2.5" />
          {{ palette.typeLabel.toUpperCase() }}
        </span>
        <span v-if="secondaryChip" class="secondary-chip" :style="secondaryChipStyle">
          {{ secondaryChip.toUpperCase() }}
        </span>
        <span v-if="variant === 'compact'" class="inline-time tnum" :style="{ color: palette.ink }">
          {{ inlineTime }}
        </span>
      </div>

      <p class="headline">{{ headline }}</p>

      <p v-if="metaParts.length > 0" class="meta-row">
        <template v-for="(part, idx) in metaParts" :key="idx">
          <span v-if="idx > 0" class="meta-bullet">·</span>
          <MapPin v-if="part.isPlace" :size="11" :stroke-width="2" class="meta-pin" />
          <span class="meta-text">{{ part.text }}</span>
        </template>
      </p>

      <div v-if="statusPills.length > 0" class="pill-row">
        <span
          v-for="(pill, idx) in statusPills"
          :key="idx"
          class="status-pill"
          :style="pillStyle(pill.tone)"
        >
          <component :is="pill.icon" :size="11" :stroke-width="2.5" />
          {{ pill.label }}
        </span>
        <div v-if="markedFraction !== null" class="progress-bar" :style="progressBarStyle">
          <div class="progress-bar-fill" :style="progressBarFillStyle" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.schedule-item {
  display: flex;
  align-items: stretch;
  width: 100%;
  cursor: pointer;
  user-select: none;
  transition: transform var(--motion-fast);
}

.schedule-item:hover .body {
  filter: brightness(0.98);
}

.schedule-item:active .body {
  transform: scale(0.997);
}

/* ── Time gutter (full only) ──────────────────────────────────────────── */
.gutter {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-start;
  width: 56px;
  padding-top: 4px;
  margin-right: var(--space-sm);
  flex-shrink: 0;
}

.gutter-primary {
  font-family: var(--font-sans);
  font-size: 13px;
  line-height: 16px;
  font-weight: var(--weight-bold);
  color: var(--ink-900);
}

.gutter-secondary {
  font-family: var(--font-sans);
  font-size: 11px;
  line-height: 14px;
  font-weight: var(--weight-medium);
  color: var(--ink-500);
  margin-top: 2px;
}

.gutter-rail-label {
  font-family: var(--font-sans);
  font-size: 9px;
  line-height: 11px;
  font-weight: var(--weight-bold);
  letter-spacing: 0.5px;
  color: var(--ink-500);
  margin-bottom: 2px;
}

/* ── Tinted body ──────────────────────────────────────────────────────── */
.body {
  flex: 1;
  border-radius: var(--radius-lg);
  border: 1px solid;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  transition: filter var(--motion-fast);
}

/* Eyebrow row */
.eyebrow-row {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  min-height: 14px;
}

.type-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-sans);
  font-size: 10px;
  line-height: 12px;
  font-weight: var(--weight-bold);
  letter-spacing: 0.5px;
}

.secondary-chip {
  display: inline-flex;
  align-items: center;
  font-family: var(--font-sans);
  font-size: 9px;
  line-height: 11px;
  font-weight: var(--weight-bold);
  letter-spacing: 0.5px;
  padding: 2px 7px;
  border-radius: var(--radius-pill);
}

.inline-time {
  margin-left: auto;
  font-family: var(--font-sans);
  font-size: 11px;
  line-height: 12px;
  font-weight: var(--weight-bold);
}

/* Headline */
.headline {
  font-family: var(--font-sans);
  font-size: 14px;
  line-height: 18px;
  font-weight: var(--weight-semibold);
  color: var(--ink-900);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Meta row */
.meta-row {
  display: flex;
  align-items: center;
  gap: 3px;
  font-family: var(--font-sans);
  font-size: 12px;
  line-height: 15px;
  color: var(--ink-600);
  margin: 2px 0 0;
  overflow: hidden;
  white-space: nowrap;
}

.meta-pin {
  color: var(--ink-500);
  flex-shrink: 0;
}

.meta-bullet {
  color: var(--ink-400);
  margin: 0 2px;
  flex-shrink: 0;
}

.meta-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Status pills */
.pill-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 5px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-sans);
  font-size: 11px;
  line-height: 13px;
  font-weight: var(--weight-semibold);
  padding: 3px 8px 3px 6px;
  border-radius: var(--radius-pill);
}

.progress-bar {
  width: 64px;
  height: 4px;
  border-radius: var(--radius-pill);
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  border-radius: var(--radius-pill);
}

/* ── Compact variant ──────────────────────────────────────────────────── */
.schedule-item-compact .body {
  padding: 8px 10px;
  gap: 2px;
}

.schedule-item-compact .headline {
  font-size: 13px;
  line-height: 16px;
}

.schedule-item-compact .meta-row {
  font-size: 11px;
  line-height: 14px;
}

.schedule-item-compact .pill-row {
  margin-top: 4px;
  gap: 4px;
}
</style>
